<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import EmojiOrIcon from 'shared/components/EmojiOrIcon.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useBrowserPhoneStore } from 'dashboard/modules/browserPhone/store/browserPhone';

export default {
  components: {
    EmojiOrIcon,
    NextButton,
  },
  props: {
    href: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      required: true,
    },
    emoji: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    showCopy: {
      type: Boolean,
      default: false,
    },
    clickToDial: {
      type: Boolean,
      default: false,
    },
    contactId: {
      type: [Number, String],
      default: null,
    },
    contactName: {
      type: String,
      default: '',
    },
    conversationId: {
      type: [Number, String],
      default: null,
    },
  },
  computed: {
    ...mapGetters({
      canUseNativeBrowserPhone: 'getCurrentAccountHasBrowserPhoneAccess',
    }),
    isNativeClickToDialEnabled() {
      return this.clickToDial && this.canUseNativeBrowserPhone;
    },
  },
  methods: {
    async onCopy(e) {
      e.preventDefault();
      await copyTextToClipboard(this.value);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
    },
    handleNumberClick(number) {
      if (!this.isNativeClickToDialEnabled) return;

      const browserPhoneStore = useBrowserPhoneStore();
      browserPhoneStore.dial({
        source: 'contact_panel',
        number,
        contactId: this.contactId,
        contactName: this.contactName,
        conversationId: this.conversationId,
      });
    },
  },
};
</script>

<template>
  <div class="w-full h-5 ltr:-ml-1 rtl:-mr-1">
    <a
      v-if="href || (value && isNativeClickToDialEnabled)"
      :href="href || '#'"
      class="flex items-center gap-2 text-n-slate-11 hover:underline"
      :title="title || value"
      @click="
        e => {
          if (isNativeClickToDialEnabled) {
            e.preventDefault();
            handleNumberClick(value);
          }
        }
      "
    >
      <EmojiOrIcon
        :icon="icon"
        :emoji="emoji"
        icon-size="14"
        class="flex-shrink-0 ltr:ml-1 rtl:mr-1"
      />
      <span
        v-if="value"
        class="overflow-hidden whitespace-nowrap text-ellipsis text-contact-inforow"
        :title="title || value"
      >
        {{ value }}
      </span>
      <span v-else class="text-sm text-n-slate-11">
        {{ $t('CONTACT_PANEL.NOT_AVAILABLE') }}
      </span>
      <NextButton
        v-if="showCopy"
        ghost
        xs
        slate
        class="ltr:-ml-1 rtl:-mr-1"
        icon="i-lucide-clipboard"
        @click="onCopy"
      />
    </a>

    <div
      v-else
      class="flex items-center gap-2 text-n-slate-11"
      :title="title || value"
    >
      <EmojiOrIcon
        :icon="icon"
        :emoji="emoji"
        icon-size="14"
        class="flex-shrink-0 ltr:ml-1 rtl:mr-1"
      />
      <span
        v-if="value"
        v-dompurify-html="value"
        class="overflow-hidden text-sm whitespace-nowrap text-ellipsis"
      />
      <span v-else class="text-sm text-n-slate-11">
        {{ $t('CONTACT_PANEL.NOT_AVAILABLE') }}
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.text-contact-inforow {
  color: #626679;
  font-family: 'Proxima Nova';
  font-size: 11px;
  font-style: normal;
  font-weight: 300;
  line-height: normal;
}
</style>
